<template>
  <nav
    v-if="bars.length"
    class="cir"
    aria-label="Gespreksindex"
    @mouseleave="onLeave"
  >
    <ol class="cir__bars" :style="{ '--cir-gap': gap }">
      <li v-for="b in bars" :key="b.key" class="cir__item">
        <button
          type="button"
          class="cir__bar"
          :class="[
            `cir__bar--${tier(b)}`,
            {
              'cir__bar--bucket': b.entries.length > 1,
              'cir__bar--active': isActive(b),
              'cir__bar--pending': b.state === 'pending',
              'cir__bar--error': b.state === 'error',
            },
          ]"
          :aria-label="barLabel(b)"
          :aria-current="isActive(b) ? 'true' : null"
          @mouseenter="onEnter(b, $event)"
          @focus="onEnter(b, $event)"
          @blur="onLeave"
          @click="$emit('select', selectTarget(b))"
        >
          <span class="cir__line" />
        </button>
      </li>
    </ol>

    <div
      v-if="hovered"
      class="cir__card"
      role="tooltip"
      :style="{ top: cardTop + 'px' }"
    >
      <template v-if="hovered.entries.length > 1">
        <div class="cir__card-q">
          {{ hovered.entries.length }} vragen
          <span v-if="hovered.time" class="cir__card-range"
            >{{ hovered.time }} – {{ hovered.timeEnd }}</span
          >
        </div>
        <ul class="cir__card-list">
          <li
            v-for="e in hovered.entries.slice(0, 4)"
            :key="e.key"
            :class="{ 'cir__card-list-active': e.key === activeKey }"
          >
            {{ e.question }}
          </li>
        </ul>
        <div v-if="hovered.entries.length > 4" class="cir__card-t">
          +{{ hovered.entries.length - 4 }} meer
        </div>
      </template>
      <template v-else>
        <div class="cir__card-q">{{ hovered.question }}</div>
        <div
          class="cir__card-s"
          :class="{ 'cir__card-s--muted': hovered.state !== 'done' }"
        >
          {{ cardSummary(hovered) }}
        </div>
        <div v-if="hovered.time" class="cir__card-t">{{ hovered.time }}</div>
      </template>
    </div>
  </nav>
</template>

<script>
import { railCapacity, bucketIndexEntries } from "@/utils/conversationIndex";

// ChatGPT-style conversation index: a stack of thin bars floating on the left
// edge of the messages column, one per exchange. Hover (or focus) shows a
// card with the question, the answer's summary and the time; click jumps
// there. Takes no layout width — the parent positions it absolutely.
//
// Sizing is measured, not guessed: a ResizeObserver on the host column sets
// the track height (TRACK_FRACTION of the column, leaving room for header and
// composer). When more exchanges exist than fit at BAR_SLOT px each,
// consecutive exchanges share a bar (see bucketIndexEntries) — nothing drops.
const HOVER_DELAY_MS = 120;
const CARD_HEIGHT_GUESS = 112;
const BAR_HEIGHT = 8;
const BAR_SLOT = 10; // bar + minimum gap
const GAP_MIN = 2;
const GAP_MAX = 12;
const TRACK_FRACTION = 0.6;
const TRACK_MIN = 120;
const HOST_RESERVED = 240; // header + composer the rail should stay clear of

function trackFor(hostHeight) {
  const h = Number(hostHeight) || 0;
  if (!h) return 0;
  return Math.max(
    TRACK_MIN,
    Math.min(Math.round(h * TRACK_FRACTION), h - HOST_RESERVED)
  );
}

export default {
  name: "ChatIndexRail",
  props: {
    // From buildConversationIndex(), each with a preformatted `time`.
    entries: { type: Array, default: () => [] },
    activeKey: { type: String, default: null },
  },
  emits: ["select"],
  data() {
    return {
      hoverKey: null,
      cardTop: 0,
      hoverTimer: null,
      // Measured track height in px; 0 until the host has been observed.
      trackHeight: 0,
      resizeObserver: null,
    };
  },
  computed: {
    track() {
      if (this.trackHeight) return this.trackHeight;
      // Before the first measurement (or without ResizeObserver) fall back
      // to the viewport, like the old fixed rule did.
      return typeof window !== "undefined"
        ? trackFor(window.innerHeight)
        : TRACK_MIN;
    },
    capacity() {
      return railCapacity(this.track, BAR_SLOT);
    },
    bars() {
      return bucketIndexEntries(this.entries || [], this.capacity);
    },
    hovered() {
      if (!this.hoverKey) return null;
      return this.bars.find((b) => b.key === this.hoverKey) || null;
    },
    // Bars spread out to GAP_MAX when few, and close ranks down to GAP_MIN so
    // the stack never grows past the measured track.
    gap() {
      const n = this.bars.length || 1;
      const g = this.track / n - BAR_HEIGHT;
      return `${Math.round(Math.min(GAP_MAX, Math.max(GAP_MIN, g)))}px`;
    },
  },
  mounted() {
    this.observeHost();
  },
  beforeUnmount() {
    clearTimeout(this.hoverTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    // The host is the positioned column the rail floats in
    // (.alice-chat__main). $el may be a comment node while there are no
    // entries; parentElement works for both.
    observeHost() {
      const host = this.$el && this.$el.parentElement;
      if (!host) return;
      this.trackHeight = trackFor(host.getBoundingClientRect().height);
      if (typeof ResizeObserver === "undefined") return;
      this.resizeObserver = new ResizeObserver((entries) => {
        const rect = entries && entries[0] && entries[0].contentRect;
        if (rect) this.trackHeight = trackFor(rect.height);
      });
      this.resizeObserver.observe(host);
    },
    isActive(b) {
      return (
        !!this.activeKey && b.entries.some((e) => e.key === this.activeKey)
      );
    },
    // Clicking a bucket jumps to the exchange in view if it is one of them,
    // otherwise to the bucket's first exchange.
    selectTarget(b) {
      return b.entries.find((e) => e.key === this.activeKey) || b.first;
    },
    barLabel(b) {
      if (b.entries.length === 1) return b.question;
      return `${b.entries.length} vragen, ${b.time || ""} – ${b.timeEnd || ""}`;
    },
    tier(b) {
      const n = b.answerLength || 0;
      if (n >= 1500) return "long";
      if (n >= 400) return "medium";
      return "short";
    },
    cardSummary(e) {
      if (e.state === "pending") return "Wordt beantwoord…";
      if (e.state === "error") return "Niet beantwoord";
      return e.summary || "";
    },
    onEnter(e, event) {
      clearTimeout(this.hoverTimer);
      const el = event && event.currentTarget;
      const rail = this.$el;
      if (el && rail) {
        const center = el.offsetTop + el.offsetHeight / 2;
        const maxTop = Math.max(0, rail.offsetHeight - CARD_HEIGHT_GUESS);
        this.cardTop = Math.min(
          maxTop,
          Math.max(0, center - CARD_HEIGHT_GUESS / 2)
        );
      }
      this.hoverTimer = setTimeout(() => {
        this.hoverKey = e.key;
      }, HOVER_DELAY_MS);
    },
    onLeave() {
      clearTimeout(this.hoverTimer);
      this.hoverKey = null;
    },
  },
};
</script>

<style scoped lang="scss">
.cir {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 30;
  display: flex;
  align-items: center;
}
.cir__bars {
  list-style: none;
  margin: 0;
  padding: 6px 0;
  display: flex;
  flex-direction: column;
  gap: var(--cir-gap, 10px);
}
.cir__item {
  display: flex;
}
.cir__bar {
  display: flex;
  align-items: center;
  width: 36px;
  height: 8px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--fg-3);
  outline: none;
}
.cir__line {
  display: block;
  height: 2px;
  width: 12px;
  background: currentColor;
  opacity: 0.4;
  transition: width 0.15s ease, opacity 0.15s ease, background 0.15s ease;
}
.cir__bar--medium .cir__line {
  width: 18px;
}
.cir__bar--long .cir__line {
  width: 24px;
}
.cir:hover .cir__line,
.cir__bar:focus-visible .cir__line {
  opacity: 0.85;
}
.cir__bar:hover .cir__line,
.cir__bar:focus-visible .cir__line {
  opacity: 1;
  color: var(--vp-black);
}
.cir__bar--active {
  color: var(--vp-black);
}
.cir__bar--active .cir__line {
  opacity: 1;
  width: 30px;
  height: 3px;
}
/* A bar standing for several exchanges is drawn heavier. */
.cir__bar--bucket .cir__line {
  height: 4px;
  opacity: 0.55;
}
.cir__bar--error {
  color: var(--vp-rood-100);
}
.cir__bar--pending .cir__line {
  animation: cir-pulse 1.2s ease-in-out infinite;
}
@keyframes cir-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

.cir__card {
  position: absolute;
  left: 44px;
  width: 280px;
  padding: 12px 14px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;
  pointer-events: none;
  /* A CSS animation rather than a Vue <transition>: it needs no JS frame
     callbacks, so it cannot get stuck half-way in a throttled tab. */
  animation: cir-card-in 0.12s ease both;
}
@keyframes cir-card-in {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
.cir__card-q {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--vp-black);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.cir__card-s {
  font-family: var(--font-body);
  font-size: 12px;
  line-height: 1.45;
  color: var(--fg-2);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.cir__card-s--muted {
  color: var(--fg-3);
  font-style: italic;
}
.cir__card-t {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--fg-3);
}
.cir__card-range {
  margin-left: 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 400;
  color: var(--fg-3);
}
.cir__card-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: var(--font-body);
  font-size: 12px;
  line-height: 1.4;
  color: var(--fg-2);
}
.cir__card-list li {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cir__card-list-active {
  color: var(--vp-black);
  font-weight: 700;
}
</style>
